<template>
    <div class="editor-task-wrapper">
        <div v-if="loading" class="loading-overlay">
            <div class="modern-loader">
                <div class="loader-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                </div>
                <p class="loading-text">{{ $t("auto.loading") }}</p>
            </div>
        </div>
        <div v-else id="Editor-task">
            <div class="editor-task">
                <div class="editor-task-content">
                    <div class="status-switcher-wrapper" v-if="currentWorkflow">
                        <TaskStatusBar
                            :status-list="pipelineStages"
                            :current-status="currentStage.name"
                            @update-status="handleStageChange"
                        />
                    </div>
                    <div class="input title-input">
                        <div class="label">{{ $t("task.title") }}</div>
                        <a-input
                            class="classic-new-input"
                            v-model:value="form.title"
                            :placeholder="$t('task.taskNumber')"
                        />
                    </div>
                    <div class="editor-task-quill-editor">
                        <quill-editor
                            style="max-height: 20%; width: 100%"
                            v-model="form.description"
                            class="quill-editor"
                            :options="editorOption"
                            :placeholder="$t('task.createTaskDesc')"
                        />
                    </div>
                    <div class="editor-task-new-checklist">
                        <div class="checklist-data">
                            <div class="checklist-header" v-if="form.checklist.length > 0">
                                <span class="checklist-title">{{ $t("auto.checklist") }}</span>
                                <a-progress
                                    class="checklist-progress"
                                    :stroke-color="{
                                        '0%': '#ec4b1d',
                                        '100%': '#ec4b1d',
                                    }"
                                    :percent="
                                        (100 / form.checklist.length) *
                                        form.checklist.filter((c) => c.isCompleted).length
                                    "
                                    :show-info="false"
                                />
                                <span class="checklist-status">
                                    {{
                                        $t("task.completedOfCheck", {
                                            total: form.checklist.length,
                                            completed: form.checklist.filter((c) => c.isCompleted).length,
                                        })
                                    }}
                                </span>
                            </div>
                            <div class="checklist-container mt-2" v-if="form.checklist.length > 0">
                                <div v-for="(checkbox, index) in form.checklist" :key="index" class="checklist-row">
                                    <div class="checklist-column">
                                        <a-checkbox class="kanban-checkbox large" v-model="checkbox.isCompleted" />
                                    </div>
                                    <div class="checklist-column">
                                        <input class="checklist-input" v-model="checkbox.title" />
                                    </div>
                                    <div class="checklist-column action">
                                        <span class="material-icons delete-icon" @click="deleteCheckbox(index)"
                                            >close</span
                                        >
                                    </div>
                                </div>
                            </div>

                            <div class="checklist-button" @click="addNewCheckBox">
                                {{ !form.checklist.length ? $t("task.newCheckList") : $t("task.add") }}
                            </div>
                        </div>
                    </div>
                    <div class="attachments-section">
                        <div class="editor-task-attach-files" @click="openFileInput">
                            <div class="attach-files">
                                <div class="text">
                                    <div class="material-icons">attachment</div>
                                    <div>{{ $t("task.attachFiles") }}</div>
                                </div>
                                <div class="logo">
                                    <s3Logo />
                                </div>
                            </div>
                            <input id="fileInput" type="file" style="display: none" @change="uploadFile" />
                        </div>
                        <div class="attachments-loading-overlay" v-if="attachmentsUploading">
                            <div class="attachments-loading-content">
                                <div class="attachments-spinner"></div>
                                <span>{{ $t("auto.uploadingFile") }}</span>
                            </div>
                        </div>
                        <div class="row attachments-row" style="display: grid; gap: 20px">
                            <div
                                class="attach-files-visibility"
                                v-for="attachment in form.attachments"
                                :key="attachment._id"
                                @click="openAttachment(attachment)"
                                style="cursor: pointer;"
                            >
                                <template v-if="['png', 'jpeg', 'jpg'].includes(attachment.ext)">
                                    <img
                                        :src="
                                            attachment.v2Format && attachment.cdnUrl
                                                ? attachment.cdnUrl
                                                : `https://static.supsis.live/${attachment.path + attachment._id}.${
                                                      attachment.ext
                                                  }`
                                        "
                                        :alt="attachment.name"
                                    />
                                </template>
                                <template v-else>
                                    <div class="file-icon">
                                        <span class="md-icon">insert_drive_file</span>
                                    </div>
                                </template>
                                <div class="image-content">
                                    <div class="text">
                                        <div v-if="editImageName === attachment._id" class="edit-img-name" @click.stop>
                                            <a-input type="text" v-model:value="attachment.name" />
                                        </div>
                                        <label v-else>{{ attachment.name }}</label>
                                    </div>
                                    <div class="description">
                                        {{ updatedAtFormated(attachment.updatedAt) }}
                                        <div class="edit-btn" @click.stop>
                                            <div class="underline" @click="deleteAttachment(attachment._id)">
                                                {{ $t("chatbotv2.delete") }}
                                            </div>
                                            |
                                            <div class="underline" @click="editImageNameFunc(attachment._id)">
                                                {{ $t("message.edit") }}
                                            </div>
                                            |
                                            <div class="underline" @click="openImage(attachment._id)">
                                                {{ $t("task.view") }}
                                            </div>
                                            |
                                            <div
                                                v-if="['png', 'jpeg', 'jpg'].includes(attachment.ext)"
                                                class="underline"
                                                @click="
                                                    form.coverPhoto === attachment._id
                                                        ? removeCoverPhoto()
                                                        : setCoverPhoto(attachment._id)
                                                "
                                            >
                                                {{
                                                    form.coverPhoto === attachment._id
                                                        ? $t("message.coverPhotoRemove")
                                                        : $t("message.coverPhotoSet")
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ChatMediaGallery
                            :visible.sync="taskGalleryVisible"
                            :items="taskGalleryItems"
                            :initial-index="taskGalleryIndex"
                        />
                    </div>
                    <div class="add-tags ml-2 mt-4">
                        <div
                            style="
                                margin-top: 10px;
                                color: #ec4b1d;
                                font-weight: bold;
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                            "
                        >
                            {{ $t("message.addTag") }}
                            <a-dropdown>
                                <a-button style="margin-left: 10px; padding: 2px">
                                    <span class="material-icons">add</span>
                                </a-button>
                                <a-menu slot="overlay">
                                    <a-menu-item
                                        v-for="tag in tags"
                                        :key="tag._id"
                                        @click="selectTag(tag)"
                                        style="display: flex; align-items: center"
                                    >
                                        <div
                                            class="showColor"
                                            :style="{
                                                backgroundColor: tag.color,
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '5px',
                                                marginRight: '8px',
                                            }"
                                        ></div>
                                        <span>{{ tag.name }}</span>
                                    </a-menu-item>
                                </a-menu>
                            </a-dropdown>
                        </div>
                        <div v-if="form.tags.length > 0" class="selected-tags">
                            <div
                                v-for="tag in form.tags"
                                :key="tag._id"
                                class="tag-item"
                                :style="getTagStyle(tag.color)"
                            >
                                <span>{{ tag.name }}</span>
                                <span class="material-icons close-icon" @click="removeTag(tag._id)">close</span>
                            </div>
                        </div>
                    </div>
                    <div class="editor-task-new-subtask ml-4">
                        <div class="row gap-10">
                            <div class="subtask-button" @click="addNewSubtask(false)">
                                {{ $t("task.addSubtask") }}
                            </div>
                            <div
                                style="margin-top: 10px; display: flex; gap: 10px; flex-direction: column"
                                v-if="form.subTasks.length > 0"
                            >
                                <div class="subtask-container">
                                    <div class="subtask-header">
                                        <span>{{ $t("task.subtaskTitle") }}</span>
                                        <span>{{ $t("task.assignee") }}</span>
                                    </div>
                                    <div v-for="(subtask, index) of form.subTasks" :key="index" class="subtask-row">
                                        <template v-if="!subtask.isExits">
                                            <div class="subtask-column">
                                                <a-checkbox class="kanban-checkbox large" v-model="subtask.completed" />
                                                <a-input
                                                    class="classic-new-input"
                                                    v-model:value="subtask.title"
                                                    :placeholder="$t('task.subtaskTitle')"
                                                />
                                            </div>
                                            <div class="subtask-column">
                                                <a-select
                                                    class="select-multiple"
                                                    v-model:value="subtask.assignees"
                                                    :options="visibilityUsers"
                                                    :placeholder="$t('task.assignee')"
                                                ></a-select>
                                            </div>
                                        </template>

                                        <template v-else>
                                            <div class="subtask-column">
                                                <a-select
                                                    class="select-multiple"
                                                    v-model:value="subtask.taskId"
                                                    :options="visibilityUsers"
                                                    :placeholder="$t('task.assignee')"
                                                ></a-select>
                                            </div>
                                        </template>

                                        <div class="subtask-column action">
                                            <span class="material-icons delete-icon" @click="deleteSubtask(index)"
                                                >close</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!--                        <div-->
                            <!--                            style="margin-top: 10px; color: #ec4b1d; font-weight: bold; cursor: pointer"-->
                            <!--                            @click="addNewSubtask(true)"-->
                            <!--                        >-->
                            <!--                            {{ $t("task.addExistsSubtask") }}-->
                            <!--                        </div>-->
                        </div>
                    </div>
                    <div class="tab-menu-container">
                        <div class="tab-header">
                            <button
                                class="tab-button"
                                :class="{ active: activeTab === 'comments' }"
                                @click="activeTab = 'comments'"
                            >
                                {{ $t("task.comments") }}
                            </button>
                            <button
                                class="tab-button"
                                :class="{ active: activeTab === 'activities' }"
                                @click="activeTab = 'activities'"
                            >
                                {{ $t("auto.activities") }}
                            </button>
                            <button
                                v-if="form.doneStatus && form.doneStatus.length > 0"
                                class="tab-button"
                                :class="{ active: activeTab === 'doneStatus' }"
                                @click="activeTab = 'doneStatus'"
                            >
                                {{ $t("task.closureStatusesTab") }}
                            </button>
                            <!-- WebTab sekmeleri -->
                            <button
                                v-for="webTab in taskDetailWebTabs"
                                :key="webTab._id"
                                class="tab-button webtab-button"
                                :class="{ active: activeTab === `webtab-${webTab._id}` }"
                                @click="activeTab = `webtab-${webTab._id}`"
                            >
                                <span class="material-icons tab-icon" :style="{ color: webTab.iconColor }">{{ webTab.icon }}</span>
                                {{ webTab.name }}
                            </button>
                        </div>

                        <div class="tab-content">
                            <div v-show="activeTab === 'comments'" class="comments-section">
                                <CreateComment :task-id="details.taskId" />
                                <listComment v-if="commentsReady" :task-id="details.taskId" />
                            </div>

                            <div v-show="activeTab === 'activities'" class="activities-section">
                                <ActivtyTask ref="activtyTask" :task-id="details.taskId" />
                            </div>

                            <div v-show="activeTab === 'doneStatus'" class="done-status-section">
                                <div v-for="(status, index) in form.doneStatus" :key="index" class="done-status-card">
                                    <div class="done-status-header">
                                        <div class="status-info">
                                            <span class="material-icons" :class="getStatusIcon(status.status)">
                                                {{ getStatusIconName(status.status) }}
                                            </span>
                                            <span class="status-text">{{ status.status }}</span>
                                        </div>
                                        <div class="status-date">
                                            {{ formatDate(status.date) }}
                                        </div>
                                    </div>
                                    <div class="done-status-content">
                                        <div class="user-info">
                                            <Avatar
                                                :size="25"
                                                :key="1"
                                                :src="status.actor.gravatar"
                                                :username="status.actor.name"
                                                v-tippy="{
                                                    followCursor: true,
                                                    placement: 'bottom-end',
                                                }"
                                                :content="status.actor.email"
                                            />
                                            <span class="user-name">{{ status.actor.name }}</span>
                                        </div>
                                        <div class="status-description">
                                            {{ status.description }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- WebTab sekmeleri içerikleri -->
                            <div
                                v-for="webTab in taskDetailWebTabs"
                                :key="`content-${webTab._id}`"
                                v-show="activeTab === `webtab-${webTab._id}`"
                                class="webtab-section"
                            >
                                <WebTabViewer
                                    :web-tab-prop="webTab"
                                    :context="form"
                                    context-type="task"
                                    embedded
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="editor-info task-sidebar-modern">
                <div class="task-sidebar-scroll">
                    <!-- Dates & Priority Section -->
                    <div class="task-section">
                        <div class="task-field">
                            <label class="task-label">{{ $t("message.startDate") }}</label>
                            <a-date-picker
                                class="task-input"
                                :suffixIcon="null"
                                dropdownClassName="task-date-picker"
                                v-model:value="form.startDate"
                                show-time
                            />
                        </div>
                        <div class="task-field">
                            <label class="task-label">{{ $t("message.endDate") }}</label>
                            <a-date-picker
                                class="task-input"
                                :suffixIcon="null"
                                dropdownClassName="task-date-picker"
                                v-model:value="form.endDate"
                                show-time
                            />
                        </div>
                        <div class="task-field">
                            <label class="task-label">{{ $t("task.priority") }}</label>
                            <a-select
                                v-model:value="form.priority"
                                class="task-input"
                                dropdownClassName="task-priority-select-dropdown"
                            >
                                <a-select-option value="low">
                                    <div class="priority-option">
                                        <LowIcon class="priority-icon" />
                                        {{ $t("message.low") }}
                                    </div>
                                </a-select-option>
                                <a-select-option value="lowest">
                                    <div class="priority-option">
                                        <LowestIcon class="priority-icon" />
                                        {{ $t("message.lowest") }}
                                    </div>
                                </a-select-option>
                                <a-select-option value="medium">
                                    <div class="priority-option">
                                        <MediumIcon class="priority-icon" />
                                        {{ $t("message.medium") }}
                                    </div>
                                </a-select-option>
                                <a-select-option value="high">
                                    <div class="priority-option">
                                        <HighIcon class="priority-icon" />
                                        {{ $t("message.high") }}
                                    </div>
                                </a-select-option>
                                <a-select-option value="highest">
                                    <div class="priority-option">
                                        <HighestIcon class="priority-icon" />
                                        {{ $t("message.highest") }}
                                    </div>
                                </a-select-option>
                            </a-select>
                        </div>
                    </div>

                    <!-- Assignment Section -->
                    <div class="task-section">
                        <div class="task-field">
                            <label class="task-label">{{ $t("task.owner") }}</label>
                            <a-select
                                class="task-input"
                                v-model:value="form.owner"
                                :options="visibilityUsers"
                                :placeholder="$t('task.owner')"
                                showSearch
                                :filter-option="filterOption"
                            />
                        </div>
                        <div class="task-field">
                            <label class="task-label">{{ $t("task.assignee") }}</label>
                            <a-select
                                class="task-input"
                                v-model:value="form.assignees"
                                mode="multiple"
                                :placeholder="$t('task.selectAssginee')"
                                :max-tag-count="maxTagCount"
                                :options="visibilityUsers"
                                :filter-option="filterOption"
                            />
                        </div>
                        <div class="task-field">
                            <label class="task-label">{{ $t("message.follower") }}</label>
                            <a-select
                                class="task-input"
                                v-model:value="form.followers"
                                mode="multiple"
                                :placeholder="$t('task.filterBar.tabFollowers')"
                                :max-tag-count="maxTagCount"
                                :options="visibilityUsers"
                                :filter-option="filterOption"
                            />
                        </div>
                    </div>

                    <!-- Metadata Section -->
                    <div class="task-section">
                        <div class="task-field-row">
                            <label class="task-label">{{ $t("task.taskID") }}</label>
                            <span class="task-value">{{ form._id }}</span>
                        </div>
                    </div>

                    <!-- Creator Section -->
                    <div class="task-section" v-if="usersMap[form.createdBy.user] || form.createdBy.type">
                        <label class="task-section-title">{{ $t("message.taskCreator") }}</label>
                        <div class="task-creator-card" v-if="usersMap[form.createdBy.user]">
                            <Avatar
                                :size="32"
                                :src="usersMap[form.createdBy.user].gravatar"
                                :username="usersMap[form.createdBy.user].name"
                            />
                            <div class="task-creator-info">
                                <div class="task-creator-name">{{ usersMap[form.createdBy.user].name || $t("auto.unknownUser") }}</div>
                                <div class="task-creator-date">{{ formattedCreatedAt }}</div>
                            </div>
                        </div>
                        <div class="task-creator-card" v-else-if="form.createdBy.type === 'api'">
                            <Avatar
                                :size="32"
                                src="https://static.supsis.live/common/email/supsisyazisislogo.png"
                                :username="$t('task.creatorApi')"
                            />
                            <div class="task-creator-info">
                                <div class="task-creator-name">{{ $t("task.creatorApi") }}</div>
                                <div class="task-creator-date">{{ formattedCreatedAt }}</div>
                            </div>
                        </div>
                        <div class="task-creator-card" v-else-if="form.createdBy.type === 'chatbot'">
                            <Avatar
                                :size="32"
                                src="https://static.supsis.live/common/email/supsisyazisislogo.png"
                                :username="$t('auto.chatbot')"
                            />
                            <div class="task-creator-info">
                                <div class="task-creator-name">{{ $t("auto.chatbot") }}</div>
                                <div class="task-creator-date">{{ formattedCreatedAt }}</div>
                            </div>
                        </div>
                        <div class="task-creator-card" v-else-if="form.createdBy.type === 'automation'">
                            <Avatar
                                :size="32"
                                src="https://static.supsis.live/common/email/supsisyazisislogo.png"
                                :username="$t('task.creatorAutomation')"
                            />
                            <div class="task-creator-info">
                                <div class="task-creator-name">{{ $t("task.creatorAutomation") }}</div>
                                <div class="task-creator-date">{{ formattedCreatedAt }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Related Records Section -->
                    <div class="task-section" v-if="tableRecords.length > 0">
                        <label class="task-section-title">{{ $t("task.relatedTableRecords") }}</label>
                        <div
                            v-for="(record, index) in tableRecords"
                            :key="record._id"
                            class="task-related-item"
                            @click="viewRecord(record)"
                        >
                            <span class="material-icons task-related-icon">table_view</span>
                            <span class="task-related-text">{{
                                $t("task.tableRecordItem", { order: index + 1, tableName: getTableName(record) })
                            }}</span>
                        </div>
                    </div>

                    <!-- Visitor Section -->
                    <div class="task-section">
                        <label class="task-section-title">{{ $t("auto.relatedVisitor") }}</label>
                        <div v-if="visitorRelation" class="task-visitor-card">
                            <div class="task-visitor-info" @click="visitorDetail">
                                <Avatar
                                    :size="32"
                                    :src="visitorRelation.visitor.gravatar"
                                    :username="visitorRelation.visitor.fullname"
                                />
                                <span class="task-visitor-name">{{ visitorRelation.visitor.fullname }}</span>
                            </div>
                            <span class="task-visitor-remove material-icons" @click="removeVisitor">close</span>
                        </div>
                        <div v-else class="task-add-visitor" @click="addVisitor">
                            <span class="material-icons">add</span>
                            <span>{{ $t("auto.addVisitor") }}</span>
                        </div>
                        <div v-if="showModal" class="custom-visitor-modal" @keydown.esc="closeModal">
                            <div class="custom-visitor-modal-backdrop" @click="closeModal"></div>
                            <div class="custom-visitor-modal-dialog">
                                <div class="custom-visitor-modal-header">
                                    <div class="title">{{ $t("task.selectPerson") }}</div>
                                    <span class="material-icons close-icon" @click="closeModal">close</span>
                                </div>
                                <div class="custom-visitor-modal-body">
                                    <List picker-mode @select="handleVisitorSelect" />
                                </div>
                                <div class="custom-visitor-modal-footer">
                                    <a-button @click="closeModal">{{ $t("message.cancel") }}</a-button>
                                    <a-button type="primary" @click="closeModal">{{ $t("message.save") }}</a-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Assignees Section -->
                    <div class="task-section" v-if="getUserIds(form).length > 0">
                        <label class="task-section-title">{{ $t("task.assignees") }}</label>
                        <div class="task-people-list">
                            <div class="task-people-item" v-for="userId of getUserIds(form)" :key="userId">
                                <Avatar
                                    :size="28"
                                    :src="usersMap[userId] && usersMap[userId].gravatar"
                                    :username="usersMap[userId] && usersMap[userId].name"
                                />
                                <span class="task-people-name">{{ (usersMap[userId] && usersMap[userId].name) || $t("auto.unknownUser") }}</span>
                                <span
                                    class="task-people-action material-icons"
                                    v-if="userId._id !== loggedUser._id"
                                    @click="startDM(usersMap[userId]._id)"
                                >
                                    mail_outline
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Owner Section -->
                    <div class="task-section" v-if="getUserIdsOwner(form).length > 0">
                        <label class="task-section-title">{{ $t("task.owner") }}</label>
                        <div class="task-people-list">
                            <div class="task-people-item" v-for="userId of getUserIdsOwner(form)" :key="userId">
                                <Avatar
                                    :size="28"
                                    :src="usersMap[userId] && usersMap[userId].gravatar"
                                    :username="usersMap[userId] && usersMap[userId].name"
                                />
                                <span class="task-people-name">{{ (usersMap[userId] && usersMap[userId].name) || $t("auto.unknownUser") }}</span>
                                <span
                                    class="task-people-action material-icons"
                                    v-if="userId._id !== loggedUser._id"
                                    @click="startDM(usersMap[userId]._id)"
                                >
                                    mail_outline
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import s3Logo from "../svg/S3.vue";
import Avatar from "vue-avatar";
import { mapActions, mapState, mapGetters } from "vuex";
import _ from "lodash";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { uploadAsset } from "../../../api/assetUpload";
import axios from "axios";
import moment from "moment/moment";
import LowIcon from "../../svg/task/LowIcon.vue";
import LowestIcon from "../../svg/task/LowestIcon.vue";
import MediumIcon from "../../svg/task/MediumIcon.vue";
import HighIcon from "../../svg/task/HighIcon.vue";
import HighestIcon from "../../svg/task/HighestIcon.vue";
import CreateComment from "../../comment/CreateComment.vue";
import listComment from "../../comment/ListComment.vue";
import TaskStatusBar from "../../../views/Pages/Tasks/components/TaskStatusBar.vue";
import List from "../../../views/Pages/Visitors/List.vue";
import ActivtyTask from "./ActivtyTask.vue";
import WebTabViewer from "../../../views/Pages/WebTabs/WebTabViewer.vue";
import ChatMediaGallery from "../../popup-chat/ChatMediaGallery.vue";

export default {
    name: "EditorTask",
    components: {
        s3Logo,
        Avatar,
        LowIcon,
        LowestIcon,
        MediumIcon,
        HighIcon,
        HighestIcon,
        CreateComment,
        listComment,
        TaskStatusBar,
        List,
        ActivtyTask,
        WebTabViewer,
        ChatMediaGallery,
    },
    props: {
        details: {
            type: Object,
        },
    },
    data() {
        return {
            loading: true,
            showModal: false,
            maxTagCount: 1,
            form: {
                title: null,
                description: null,
                priority: "low",
                checklist: [],
                assigneeIds: [],
                ownerId: null,
                collaboratorIds: [],
                followerIds: [],
                subTasks: [],
                startDate: null,
                endDate: null,
                workflowId: null,
                pipelineStageId: null,
                color: null,
                createdAt: null,
                attachments: [],
                createdBy: {
                    type: null,
                    user: null,
                },
                tags: [],
                coverPhoto: null,
            },
            editorOption: {
                theme: "snow",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
                        ["link"],
                    ],
                },
            },
            editImageName: null,
            isSaved: false,
            taskGalleryVisible: false,
            taskGalleryIndex: 0,
            tags: [],
            activeTab: "comments",
            attachmentsUploading: false,
            commentsReady: false,
        };
    },
    computed: {
        ...mapState({
            loggedUser: (state) => state.auth.user || state.auth.adminUser,
            usersMap: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc.push({
                        value: user._id,
                        label: user.name,
                    });
                    return acc;
                }, []),
            defaultWorkflow: (state) => state.tasks.defaultWorkflow,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
            tables: (state) => state.table.tables,
        }),
        ...mapGetters({
            taskDetailWebTabs: "webtabs/getTaskDetailWebTabs",
        }),
        taskGalleryItems() {
            var attachments = this.form && this.form.attachments ? this.form.attachments : [];
            return attachments.map(function (att) {
                var url = att.v2Format && att.cdnUrl
                    ? att.cdnUrl
                    : "https://static.supsis.live/" + att.path + att._id + (att.ext ? "." + att.ext : "");
                var ext = (att.ext || "").toLowerCase();
                var mime = (att.mime || "").toLowerCase();
                if (!mime && ["png", "jpeg", "jpg", "gif", "webp", "svg"].indexOf(ext) !== -1) {
                    mime = "image/" + (ext === "jpg" ? "jpeg" : ext);
                }
                return {
                    url: url,
                    name: att.name || "Dosya",
                    mime: mime,
                    ext: ext,
                };
            });
        },
        visitorRelation() {
            return this.form?.relations?.find((relation) => relation.type === "visitor");
        },
        tableRecords() {
            return this.form?.relations?.filter((relation) => relation.type === "tableRecord") || [];
        },
        formattedCreatedAt() {
            return moment(this.form.createdAt).format("DD/MM/YYYY HH:mm");
        },
        visibilityUsers() {
            const workflow = this.workflows[this.form.workflow];
            if (workflow && workflow.visibility && workflow.visibility.users && workflow.visibility.users.length > 0) {
                return this.users.filter((user) => workflow.visibility.users.includes(user.value));
            } else {
                return this.users;
            }
        },
        currentWorkflow() {
            return this.workflows[this.form.workflow];
        },
        currentStage() {
            return this.currentWorkflow?.pipeline.find((p) => p._id === this.form.pipelineStage) || {};
        },
        pipelineStages() {
            if (!this.currentWorkflow) return [];

            return this.currentWorkflow.pipeline.map((stage) => ({
                title: stage.name,
                label: stage.name,
                type: stage.type,
                _id: stage._id,
            }));
        },
    },
    methods: {
        ...mapActions({
            getTags: "tag/getTags",
            getTaskDetail: "tasks/getTaskDetail",
            attachmentsDelete: "tasks/attachmentsDelete",
            addNewWindow: "app/addNewWindow",
        }),
        getTableName(relation){
            return this.tables.find((table) => table._id === relation.tableRecord?.table)?.name;
        },
        visitorDetail() {
            this.addNewWindow({
                type: "show-visitor",
                title: this.$t("task.visitorProfileWindowTitle", {
                    name: this.visitorRelation.visitor.fullname || this.$t("auto.unknownUser"),
                }),
                payload: {
                    visitorId: this.visitorRelation.visitor._id,
                },
                footer: false,
            });
        },
        async getTaskTags() {
            try {
                this.tags = await this.getTags("task");
            } catch (error) {
                console.error("Error fetching tags:", error);
            }
        },
        setCoverPhoto(attachmentId) {
            this.form.coverPhoto = attachmentId;
            this.$toasted.success(this.$t("task.coverPhotoToastSet"), {
                duration: 3000,
            });
        },
        removeCoverPhoto() {
            this.form.coverPhoto = null;
            this.$toasted.success(this.$t("task.coverPhotoToastRemoved"), {
                duration: 3000,
            });
        },
        getTagStyle(color) {
            const isLight = this.isLightColor(color);
            return {
                backgroundColor: color,
                color: isLight ? "#000" : "#fff",
            };
        },
        isLightColor(color) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 155;
        },
        getSelectedTagIds() {
            return this.form.tags.map((tag) => tag._id);
        },
        selectTag(tag) {
            const isExists = this.form.tags.find((selectedTag) => selectedTag._id === tag._id);
            if (isExists) {
                this.form.tags = this.form.tags.filter((selectedTag) => selectedTag._id !== tag._id);
            } else {
                this.form.tags = _.uniq([...this.form.tags, tag]);
            }
        },
        removeTag(tagId) {
            this.form.tags = this.form.tags.filter((tag) => tag._id !== tagId);
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        addNewCheckBox() {
            if (
                this.form.checklist[this.form.checklist.length - 1] &&
                !this.form.checklist[this.form.checklist.length - 1].title
            ) {
                this.form.checklist = this.form.checklist.slice(0, this.form.checklist.length - 1);
            } else {
                this.form.checklist = [
                    ...this.form.checklist,
                    {
                        isCompleted: false,
                        title: null,
                    },
                ];
            }
        },
        deleteCheckbox(index) {
            this.form.checklist = this.form.checklist.filter((c, i) => i !== index);
        },
        addNewSubtask(isExits) {
            if (
                this.form.subTasks[this.form.subTasks.length - 1] &&
                (this.form.subTasks[this.form.subTasks.length - 1].isExits
                    ? !this.form.subTasks[this.form.subTasks.length - 1].taskId
                    : !this.form.subTasks[this.form.subTasks.length - 1].title)
            ) {
                this.form.subTasks = this.form.subTasks.slice(0, this.form.subTasks.length - 1);
            } else {
                this.form.subTasks = [
                    ...this.form.subTasks,
                    isExits
                        ? {
                              isExits,
                              taskId: null,
                          }
                        : {
                              completed: false,
                              assignees: this.users[0]?.value,
                              title: null,
                          },
                ];
            }
        },
        deleteSubtask(index) {
            this.form.subTasks = this.form.subTasks.filter((c, i) => i !== index);
        },
        updatedAtFormated(a) {
            const date = this.$moment(a).format("DD.MM.YYYY HH:mm");
            return date;
        },
        getUserIds(form) {
            return _.uniq([form.assignees].flat()).filter((c) => c);
        },
        getUserIdsOwner(form) {
            return _.uniq([form.owner].flat()).filter((c) => c);
        },
        getCreatedBy(form) {
            if (form.createdBy.type === "api") {
                return "API";
            } else {
                return this.usersMap[form.createdBy.user];
            }
        },
        startDM(id) {
            this.$router.push("/chat?tab=teamChat");
            this.$emit("close-modal");
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.START_AGENT_CHAT, { userId: id });
            }, 500);
        },
        openFileInput() {
            document.getElementById("fileInput").click();
        },
        beforeUpload(file) {
            // const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png";
            // if (!isJpgOrPng) {
            //     this.$toasted.show("Sadece Resim Yükleyebilirsiniz!", {
            //         duration: 3000,
            //     });
            // }
            const isLt5M = file.size / 1024 / 1024 < 50;
            if (!isLt5M) {
                this.$toasted.show(this.$t("message.imageSizeLimit"), {
                    duration: 3000,
                });
            }
            // return isJpgOrPng && isLt5M;
            return isLt5M;
        },
        async uploadFile(e) {
            const input = document.getElementById(e.target.id);
            if (!input || !input.files || !input.files[0]) {
                return;
            }
            const file = input.files[0];
            if (!this.beforeUpload(file)) return;
            this.attachmentsUploading = true;
            let path = `${this.site._id}/tasks-attachments/`;
            try {
                let imdUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "task-attachment",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                await axios({
                    method: imdUploadData.data.method,
                    url: imdUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });
                this.$toasted.success(this.$t("message.successImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
                this.form.attachments = [...this.form.attachments, imdUploadData.data.asset];
            } catch (e) {
                this.$toasted.error(this.$t("message.errorImgUpload"), {
                    duration: 1000,
                    position: "bottom-center",
                });
            } finally {
                this.attachmentsUploading = false;
                if (input) {
                    input.value = "";
                }
            }
        },
        async deleteAttachment(id) {
            this.form.attachments = this.form.attachments.filter((c) => c.id !== id);
            if (this.details?.taskId) {
                this.attachmentsDelete({ taskId: this.details?.taskId, attachmentId: id });
            } else {
                this.attachmentsDelete({ attachmentId: id });
            }
        },
        editImageNameFunc(id) {
            this.editImageName = this.editImageName !== id && id;
        },
        openAttachment(attachment) {
            var idx = this.form.attachments.findIndex(function (a) { return a._id === attachment._id; });
            if (idx === -1) idx = 0;
            this.taskGalleryIndex = idx;
            this.taskGalleryVisible = true;
        },
        openImage(id) {
            var idx = this.form.attachments.findIndex(function (a) { return a._id === id; });
            if (idx === -1) idx = 0;
            this.taskGalleryIndex = idx;
            this.taskGalleryVisible = true;
        },
        openUrlInNewTab(attachment) {
            const url =
                attachment.v2Format && attachment.cdnUrl
                    ? attachment.cdnUrl
                    : `https://static.supsis.live/${attachment.path + attachment._id}.${attachment.ext}`;
            window.open(url, "_blank");
        },
        async handleStageChange(stageName) {
            const stage = this.currentWorkflow.pipeline.find((p) => p.name === stageName);
            if (stage && stage._id !== this.form.pipelineStage) {
                this.form.pipelineStage = stage._id;
            }
        },
        addVisitor() {
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        handleVisitorSelect(visitor) {
            console.log("visitorrrrrr", visitor);
            if (!this.form.relations) {
                this.form.relations = [];
            }

            const visitorRelation = {
                type: "visitor",
                visitor: {
                    _id: visitor._id,
                    fullname: visitor.fullname,
                    gravatar: visitor.gravatar || visitor.avatar,
                },
            };

            const existingIndex = this.form.relations.findIndex((rel) => rel.type === "visitor");
            if (existingIndex !== -1) {
                this.form.relations[existingIndex] = visitorRelation;
            } else {
                this.form.relations.push(visitorRelation);
            }

            this.closeModal();
            this.$message.success(this.$t("task.visitorLinkedSuccess"));
        },
        removeVisitor() {
            if (this.form.relations) {
                this.form.relations = this.form.relations.filter((rel) => rel.type !== "visitor");
                this.$message.success(this.$t("task.visitorUnlinkedSuccess"));
            }
        },
        viewRecord(relation) {
            if (relation && relation.tableRecord) {
                this.addNewWindow({
                    id: relation.tableRecord,
                    type: "table-record-viewer",
                    title: this.form.title,
                    payload: {
                        tableId: relation.tableRecord.table,
                        recordId: relation.tableRecord._id,
                    },
                    multiple: true,
                    autoClose: false,
                });
            } else {
                console.error("No tableRecord relation found");
            }
        },
        getStatusIcon(status) {
            switch (status) {
                case "Won":
                    return "status-won";
                case "Lost":
                    return "status-lost";
                case "Paused":
                    return "status-paused";
                default:
                    return "";
            }
        },
        getStatusIconName(status) {
            switch (status) {
                case "Won":
                    return "check_circle";
                case "Lost":
                    return "cancel";
                case "Paused":
                    return "pause_circle";
                default:
                    return "help";
            }
        },
        formatDate(date) {
            return this.$moment(date).format("DD/MM/YYYY HH:mm");
        },
    },
    mounted() {
        this.getTaskTags();
        this.getTaskDetail(this.details.taskId)
            .then((data) => {
                this.form = data;
            })
            .catch((error) => {
                console.error("Task load error:", error);
                this.$message.error(this.$t("auto.taskLoadFailed"));
            })
            .finally(() => {
                this.loading = false;
                this.$nextTick(() => {
                    this.commentsReady = true;
                });
            });
    },
    watch: {
        activeTab(newTab) {
            if (newTab === 'activities' && this.$refs.activtyTask) {
                this.$refs.activtyTask.loadActivities();
            }
        },
        form: {
            handler() {
                this.$emit("update-form", {
                    ...this.form,
                    attachments: this.form.attachments,
                    tags: this.getSelectedTagIds(),
                });
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss">
.task-date-picker {
    z-index: 999999;
}

.task-priority-select-dropdown {
    .priority-option {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }

    .priority-icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
    }
}
#Editor-task {
    display: flex;
    flex-direction: row;
    width: 100%;
    min-height: 100%;
    margin-top: -20px;
    gap: 0;

    .subtask-container {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
    }

    .subtask-header {
        display: grid;
        grid-template-columns: 1fr 1fr 80px;
        background: #f9f9f9;
        padding: 10px;
        font-weight: bold;
        border-radius: 5px;
        text-align: center;
    }

    .subtask-row {
        display: grid;
        grid-template-columns: 1fr 1fr 80px;
        align-items: center;
        padding: 10px;
        border-bottom: 1px solid #eee;
    }

    .subtask-column {
        display: flex;
        align-items: center;
        gap: 10px;
        justify-content: center;
    }

    .action {
        display: flex;
        justify-content: center;
    }

    .delete-icon {
        cursor: pointer;
        color: red;
        transition: transform 0.2s ease;
    }

    .delete-icon:hover {
        transform: scale(1.2);
    }

    .checklist-button {
        border: 1px solid #f1f1f1;
        padding: 15px;
        border-radius: 10px;
        margin-top: 10px;
        color: rgb(236, 75, 29);
        font-weight: bold;
        cursor: pointer;
        width: 32vh;
        text-align: center;
        background-color: #fff;
        transition: all 0.3s ease-in-out;
    }

    .checklist-button:hover {
        background-color: rgba(236, 75, 29, 0.1);
        border-color: rgba(236, 75, 29, 0.6);
        color: rgb(200, 50, 20);
        transform: scale(1.02);
    }

    .subtask-button {
        border: 1px solid #f1f1f1;
        padding: 15px;
        border-radius: 10px;
        margin-top: 10px;
        color: rgb(236, 75, 29);
        font-weight: bold;
        cursor: pointer;
        width: 32vh;
        text-align: center;
        background-color: #fff;
        transition: all 0.3s ease-in-out;
    }

    .subtask-button:hover {
        background-color: rgba(236, 75, 29, 0.1);
        border-color: rgba(236, 75, 29, 0.6);
        color: rgb(200, 50, 20);
        transform: scale(1.02);
    }
    .editor-task-new-checklist {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .checklist-header {
        display: flex;
        align-items: center;
        gap: 15px;
        font-size: 16px;
        font-weight: bold;
    }

    .checklist-progress {
        width: 200px;
    }

    .checklist-status {
        width: 200px;
        font-size: 14px;
        color: #666;
    }

    .checklist-container {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .checklist-row {
        display: grid;
        grid-template-columns: 50px 1fr 60px;
        align-items: center;
        padding: 10px;
        border-bottom: 1px solid #eee;
    }

    .checklist-header-row {
        font-weight: bold;
        background: #f9f9f9;
        border-radius: 5px;
        padding: 10px;
        display: grid;
        grid-template-columns: 50px 1fr 60px;
        text-align: center;
    }

    .checklist-header-row span {
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .checklist-column {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .checklist-input {
        width: 100%;
        border: none;
        outline: none;
        background: #f1f1f1;
        border-radius: 4px;
        padding: 5px 10px;
    }

    .editor-task {
        width: 75% !important;
        min-height: 100%;
        border-right: 0px solid !important;

        .editor-task-header {
            width: 95%;
            height: auto;
            background-color: #fff;
            border-bottom: 1px solid #dddddd;
            display: flex;
            align-items: center;
            padding: 0 20px;
            font-size: 16px;
            font-weight: 500;
            color: #333333;

            .editor-task-todo-select {
                width: 200px !important;
                height: 70px !important;
                margin-bottom: -20px !important;
                margin-top: 20px !important;
            }

            .editor-re-open-btn {
                width: 110px !important;
                margin-left: 15px;
            }
        }

        .editor-task-content {
            width: 100%;
            background-color: #fff;
            padding: 20px;

            .editor-task-quill-editor {
                width: 100%;
                height: 100%;
                background-color: #fff;
                border: 1px solid #dddddd;
                border-radius: 5px;

                .quill-editor {
                    .ql-toolbar.ql-snow {
                        background: #f1f1f1 !important;
                        border-radius: 4px 4px 0px 0px;
                        border: none;
                    }

                    .ql-container.ql-snow {
                        height: 200px;
                        max-height: 200px;
                        min-height: 200px;
                        background: #f1f1f1;
                        border-radius: 0px 0px 4px 4px;
                        overflow: hidden;
                        border: none !important;
                    }
                }
            }

            .editor-task-new-checklist {
                width: 100%;
                height: 100%;
                background-color: #fff;
                border-radius: 5px;
                margin-top: 20px;

                .checklist-data {
                    width: 100%;

                    .ant-progress-success-bg,
                    .ant-progress-bg {
                        background-color: #ec4b1d !important;
                    }

                    .kanban-checkbox {
                        .ant-checkbox-inner {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 20px;
                            height: 20px;

                            &::after {
                                border: 2px solid #fff;
                                left: 20%;
                                width: 7px;
                                height: 11px;
                                border-top: 0;
                                border-left: 0;
                            }
                        }

                        .ant-checkbox.ant-checkbox-checked {
                            .ant-checkbox-inner {
                                border: 3px solid #ec4b1d !important;
                                background-color: #ec4b1d !important;
                            }
                        }

                        .ant-checkbox-checked::after {
                            border: 1px solid #ec4b1d !important;
                        }

                        .ant-checkbox-wrapper:hover .ant-checkbox-inner,
                        .ant-checkbox:hover .ant-checkbox-inner,
                        .ant-checkbox-input:focus + .ant-checkbox-inner {
                            border: 1px solid #ec4b1d !important;
                        }
                    }
                }
            }

            .editor-task-attach-files {
                cursor: pointer;
                width: 100%;
                height: 100%;
                background-color: #fff;
                margin-top: 20px;
                padding-bottom: 20px;
                border-bottom: 1px solid #dddddd;

                .attach-files {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 240px;
                    border: 1px solid #f1f1f1;
                    padding: 3px 20px;
                    border-radius: 10px;
                    margin-top: 15px;
                    font-weight: bold;

                    .text {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .logo svg {
                        width: 40px;
                    }
                }
            }

            .attachments-section {
                position: relative;
                padding-bottom: 20px;
            }

            .attachments-row {
                position: relative;
                z-index: 1;
            }

            .attachments-loading-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255, 255, 255, 0.9);
                z-index: 2;
            }

            .attachments-loading-content {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 16px 24px;
                border-radius: 10px;
                background: #fff;
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
                font-weight: 600;
                color: #ec4b1d;
            }

            .attachments-spinner {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                border: 3px solid #ffe0d6;
                border-top-color: #ec4b1d;
                animation: spin 1s linear infinite;
            }

            .editor-task-new-subtask {
                width: 100%;
                height: 100%;
                background-color: #fff;
                margin-top: 10px;
                padding-bottom: 20px;
            }

            .input {
                font-weight: bold;

                .label {
                    font-size: 16px;
                    margin-bottom: 4px;
                }

                &.title-input {
                    width: 100%;
                    margin-bottom: 20px;
                }

                &.priority-input {
                    width: 20%;
                }
            }
        }
    }

    .editor-info {
        width: 30%;
        background-color: #fff;

        .info-task-list {
            display: flex;
            flex-direction: column;
            height: 100vh;
            padding: 20px;

            .add-calendar {
                background-color: #f1f1f1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 16px;
                color: #333333;
                cursor: pointer;
                margin-bottom: 10px;
                padding: 3px;
                border-radius: 10px;
            }

            .info-task-item {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                padding: 10px 0;
                border-bottom: 1px solid #dddddd;

                label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #333333;
                }

                .info-task-item-value {
                    font-size: 14px;
                    font-weight: 400;
                    color: #333333;
                }

                .task-avatar-message {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    margin: 3px;
                }

                .task-avatar-message label {
                    width: 100px;
                    font-size: 14px;
                    font-weight: 400;
                    color: #333333;
                }

                .task-avatar-message .msg-icon {
                    color: #f76036;
                    cursor: pointer;
                    margin-left: auto;
                }

                .task-avatar-message:hover {
                    background-color: #f0f0f0;
                }

                .create-task-avatar-message {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 5px;
                    margin-bottom: 10px;
                    background-color: #f9f9f9;
                }

                .create-task-avatar-message label {
                    flex-grow: 1;
                    margin-left: 10px;
                    font-size: 14px;
                    color: #333;
                    text-align: center;
                }

                .create-task-avatar-message Avatar {
                    border-radius: 50%;
                    border: 2px solid #ddd;
                }
            }
        }
    }

    // Modern Task Sidebar
    .task-sidebar-modern {
        width: 100%;
        background: #fafafa;
        border-left: 1px solid #e8e8e8;

        .task-sidebar-scroll {
            padding: 16px;
        }

        .task-section {
            margin-bottom: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid #e8e8e8;

            &:last-child {
                border-bottom: none;
                margin-bottom: 0;
                padding-bottom: 0;
            }
        }

        .task-section-title {
            display: block;
            font-size: 14px;
            font-weight: 600;
            color: #595959;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 12px;
        }

        .task-field {
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        .task-field-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
        }

        .task-label {
            display: block;
            font-size: 14px;
            font-weight: 500;
            color: #595959;
            margin-bottom: 6px;
        }

        .task-value {
            font-size: 15px;
            color: #595959;
            word-break: break-all;
        }

        .task-input {
            width: 100% !important;
            font-size: 15px;
        }

        // Ant Design Overrides
        .ant-picker {
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            height: 36px;

            &:hover {
                border-color: #bfbfbf;
            }

            &:focus,
            &.ant-picker-focused {
                border-color: #ec4b1d;
                box-shadow: 0 0 0 2px rgba(236, 75, 29, 0.1);
            }

            .ant-picker-input > input {
                font-size: 15px;
            }
        }

        .ant-select {
            &:not(.ant-select-customize-input) .ant-select-selector {
                border-radius: 6px;
                border: 1px solid #d9d9d9;
                min-height: 36px;

                .ant-select-selection-search-input {
                    height: 34px;
                }

                .ant-select-selection-item {
                    line-height: 34px;
                    font-size: 15px;
                }

                .ant-select-selection-placeholder {
                    line-height: 34px;
                    font-size: 15px;
                    color: #bfbfbf;
                }
            }

            &.ant-select-multiple .ant-select-selector {
                padding: 4px 8px;

                .ant-select-selection-item {
                    height: 28px;
                    line-height: 26px;
                    font-size: 14px;
                    background: #fff;
                    border: 1px solid #d9d9d9;
                    border-radius: 4px;
                    margin: 2px 4px 2px 0;
                }
            }

            &:not(.ant-select-disabled):hover .ant-select-selector {
                border-color: #bfbfbf;
            }

            &.ant-select-focused .ant-select-selector {
                border-color: #ec4b1d !important;
                box-shadow: 0 0 0 2px rgba(236, 75, 29, 0.1) !important;
            }
        }

        .priority-option {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 8px;
        }

        .priority-icon {
            flex-shrink: 0;
            width: 18px;
            height: 18px;
        }

        // Creator Card
        .task-creator-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            background: #fff;
            border-radius: 8px;
            border: 1px solid #e8e8e8;
        }

        .task-creator-info {
            flex: 1;
            min-width: 0;
        }

        .task-creator-name {
            font-size: 15px;
            font-weight: 500;
            color: #262626;
            margin-bottom: 2px;
        }

        .task-creator-date {
            font-size: 13px;
            color: #8c8c8c;
        }

        // Related Items
        .task-related-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 12px;
            background: #fff;
            border-radius: 6px;
            border: 1px solid #e8e8e8;
            margin-bottom: 8px;
            cursor: pointer;
            transition: all 0.2s;

            &:last-child {
                margin-bottom: 0;
            }

            &:hover {
                border-color: #ec4b1d;
                background: #fffbf9;
            }
        }

        .task-related-icon {
            font-size: 18px;
            color: #8c8c8c;
        }

        .task-related-text {
            font-size: 15px;
            color: #595959;
            flex: 1;
        }

        // Visitor Card
        .task-visitor-card {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 12px;
            background: #fff;
            border-radius: 6px;
            border: 1px solid #e8e8e8;
        }

        .task-visitor-info {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;
            cursor: pointer;
        }

        .task-visitor-name {
            font-size: 15px;
            color: #262626;
            font-weight: 500;
        }

        .task-visitor-remove {
            font-size: 18px;
            color: #bfbfbf;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s;

            &:hover {
                color: #ff4d4f;
                background: #fff1f0;
            }
        }

        .task-add-visitor {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px 12px;
            background: #fff;
            border: 1px dashed #d9d9d9;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 15px;
            color: #8c8c8c;

            .material-icons {
                font-size: 20px;
            }

            &:hover {
                border-color: #bfbfbf;
                color: #595959;
                background: #fafafa;
            }
        }

        // People List
        .task-people-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .task-people-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 10px;
            background: #fff;
            border-radius: 6px;
            border: 1px solid #e8e8e8;
            transition: all 0.2s;

            &:hover {
                background: #fafafa;
            }
        }

        .task-people-name {
            font-size: 15px;
            color: #262626;
            flex: 1;
        }

        .task-people-action {
            font-size: 18px;
            color: #bfbfbf;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s;

            &:hover {
                color: #ec4b1d;
                background: #fffbf9;
            }
        }
    }

    .classic-new-input {
        padding: 20px;
        background: #f1f1f1;
        border: none;
        color: #3c3c3c;
        font-weight: bold;
        font-size: 14px;

        &:focus {
            border: #ec4a1d65 !important;
            box-shadow: 0 0 0 2px #ffaa0037;
        }

        &::placeholder {
            opacity: 1;
            font-weight: bold;
        }
    }

    .attach-files-visibility {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 15px;

        .image-content {
            display: inline-grid;
            align-items: center;
            gap: 5px;

            .text {
                display: flex;
                align-items: center;
                gap: 10px;

                label {
                    font-size: 12px;
                    font-weight: bold;
                    color: #333333;
                }
            }

            .description {
                cursor: pointer;
                font-size: 12px;
                color: #333333;

                .edit-btn {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: #ec4b1d;
                    font-weight: bold;
                    font-size: 12px;
                    cursor: pointer;

                    .underline {
                        text-decoration: underline;
                    }
                }
            }
        }

        img {
            object-fit: cover;
            width: 100px;
            height: auto;
        }

        .text {
            display: flex;
            align-items: center;
            gap: 5px;
        }

        label {
            margin-bottom: 0px;
        }
    }

    .ant-select-selection {
        height: 30px !important;
        overflow: hidden;
    }

    .selected-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 10px;
    }

    .tag-item {
        display: flex;
        align-items: center;
        padding: 5px 10px;
        border-radius: 20px;
        color: #fff;
        font-weight: bold;
    }

    .close-icon {
        margin-left: 10px;
        cursor: pointer;
    }
}

#task-attachment-image {
    .ant-modal {
        z-index: 99999 !important;
        width: 52vw !important;
    }

    .ant-modal-wrap {
        z-index: 99999 !important;
    }

    .ant-modal-close {
        .ant-modal-close-x {
            color: white;
            position: absolute;
        }
    }

    .ant-modal-content {
        border-radius: 4px;
    }

    .ant-modal-body {
        padding: 0 !important;
    }

    img {
        width: 100%;
        height: 100%;
    }

    .ant-modal-mask {
        z-index: 99999 !important;
        background-color: rgba(0, 0, 0, 0.75);
    }
}

.status-switcher-wrapper {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 4px 0;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 2px;

        &:hover {
            background: #999;
        }
    }
}

.add-visitor {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #ec4b1d;
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.3s;

    &:hover {
        background-color: #f5f5f5;
    }

    .material-icons {
        font-size: 20px;
    }

    span {
        font-weight: 500;
    }
}

.task-avatar-message {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.3s;

    &:hover {
        background-color: #f5f5f5;
    }

    .visitor-info {
        display: flex;
        align-items: center;
        gap: 7px;
        cursor: pointer;
    }

    .delete-icon {
        cursor: pointer;
        color: #999;
        transition: color 0.3s;

        &:hover {
            color: #ec4b1d;
        }

        .material-icons {
            font-size: 18px;
        }
    }
}

.custom-visitor-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1045;

    &-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.45);
    }

    &-dialog {
        position: relative;
        width: 80vw;
        max-height: 80vh;
        background: #ffffff;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: 1;
    }

    &-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 24px;
        border-bottom: 1px solid #e8e8e8;

        .title {
            font-size: 20px;
            font-weight: 600;
            color: #1f1f1f;
        }

        .close-icon {
            cursor: pointer;
            font-size: 22px;
            color: #8c8c8c;

            &:hover {
                color: #ec4b1d;
            }
        }
    }

    &-body {
        padding: 24px;
        overflow-y: auto;
        background: #fafafa;
    }

    &-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 16px 24px;
        border-top: 1px solid #e8e8e8;
        background: #fff;
    }
}

.editor-task-wrapper {
    position: relative;
    min-height: 500px;

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(8px);
        z-index: 1000;

        .modern-loader {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;

            .loader-spinner {
                position: relative;
                width: 80px;
                height: 80px;

                .spinner-ring {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    border: 3px solid transparent;
                    border-top-color: #ec4b1d;
                    animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;

                    &:nth-child(1) {
                        animation-delay: 0s;
                        border-top-color: #ec4b1d;
                    }

                    &:nth-child(2) {
                        width: 90%;
                        height: 90%;
                        top: 5%;
                        left: 5%;
                        animation-delay: -0.5s;
                        border-top-color: #ff7849;
                    }

                    &:nth-child(3) {
                        width: 80%;
                        height: 80%;
                        top: 10%;
                        left: 10%;
                        animation-delay: -1s;
                        border-top-color: #ffa07a;
                    }
                }
            }

            .loading-text {
                margin: 0;
                color: #ec4b1d;
                font-size: 16px;
                font-weight: 600;
                letter-spacing: 0.5px;
                animation: pulse 1.5s ease-in-out infinite;
            }
        }
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

.tab-menu-container {
    margin: 20px 0;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;

    .tab-header {
        display: flex;
        background: #fafafa;
        border-bottom: 1px solid #e8e8e8;

        .tab-button {
            padding: 12px 24px;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            color: #666;
            transition: all 0.3s ease;
            position: relative;

            &.active {
                color: #ec4b1d;
                font-weight: 600;

                &::after {
                    content: "";
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: #ec4b1d;
                }
            }

            &:hover:not(.active) {
                background: rgba(236, 75, 29, 0.05);
                color: #ec4b1d;
            }

            &.webtab-button {
                display: flex;
                align-items: center;
                gap: 6px;

                .tab-icon {
                    font-size: 16px;
                }
            }
        }
    }

    .tab-content {
        padding: 20px;
        background: #fff;

        .activities-section {
            h1 {
                font-size: 18px;
                color: #333;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 1px solid #eee;
            }
        }

        .done-status-section {
            .done-status-card {
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 8px;
                padding: 16px;
                margin-bottom: 16px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                transition: all 0.3s ease;

                &:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }

                .done-status-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #f0f0f0;

                    .status-info {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .material-icons {
                            font-size: 20px;

                            &.status-won {
                                color: #52c41a;
                            }

                            &.status-lost {
                                color: #ff4d4f;
                            }

                            &.status-paused {
                                color: #faad14;
                            }
                        }

                        .status-text {
                            font-weight: 600;
                            color: #333;
                        }
                    }

                    .status-date {
                        color: #8c8c8c;
                        font-size: 13px;
                    }
                }

                .done-status-content {
                    .user-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        margin-bottom: 8px;

                        .user-name {
                            font-weight: 500;
                            color: #333;
                        }
                    }

                    .status-description {
                        color: #595959;
                        font-size: 14px;
                        line-height: 1.5;
                        padding: 8px 0;
                        margin-left: 35px;
                        border-radius: 4px;
                    }
                }
            }
        }

        .webtab-section {
            min-height: 400px;
            border-radius: 8px;
            overflow: hidden;
        }
    }
}
</style>
